import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../api/client.ts"
import type { CaseListItem, CaseListResponse, Shipment } from "../api/types.ts"
import { useApiQuery, useAsyncAction } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { useSession } from "../auth/SessionProvider.tsx"
import { ShipmentBadge } from "../components/badges.tsx"
import { caseColumns } from "../components/case-columns.tsx"
import { useCaseDetailPanel } from "../components/CaseDetailPanel.tsx"
import { formatWeight, nullable } from "../components/format.ts"
import {
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNote,
  PageHeader,
  Skeleton,
  type Column,
} from "../ui/index.ts"

export function ShipmentListPage() {
  const { t, locale } = useI18n()
  const { user } = useSession()
  const navigate = useNavigate()
  const [picked, setPicked] = useState<Set<number>>(new Set())

  const canShip = user?.role === "warehouse" || user?.role === "admin"

  const shipments = useApiQuery<{ items: Shipment[] }>("/api/shipments")
  // 묶을 건 목록은 선적을 만들 수 있는 사람에게만 필요하다.
  const ready = useApiQuery<CaseListResponse>(
    canShip ? "/api/cases?card=ready_to_ship&size=200" : null,
  )
  const create = useAsyncAction()

  const reload = async () => {
    await Promise.all([shipments.reload(), ready.reload()])
  }

  const detail = useCaseDetailPanel({ onChanged: () => void reload(), rows: ready.data?.items })

  function toggle(id: number) {
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function createShipment() {
    const ok = await create.run(() => api.post("/api/shipments", { caseIds: [...picked] }))
    if (!ok) return
    setPicked(new Set())
    await reload()
  }

  const cols = caseColumns(t, locale)
  const readyColumns: Column<CaseListItem>[] = [
    {
      key: "pick",
      header: "",
      render: (row) => (
        <input
          type="checkbox"
          className="row-check"
          checked={picked.has(row.id)}
          aria-label={`${row.orderNo}`}
          onChange={() => toggle(row.id)}
          // 체크는 행 클릭(옆판 열기)과 별개다.
          onClick={(event) => event.stopPropagation()}
        />
      ),
    },
    cols.seq(),
    cols.orderNo({ primary: true }),
    cols.customerName(),
    cols.org({ priority: "lg" }),
    cols.weight(),
  ]

  const shipmentColumns: Column<Shipment>[] = [
    {
      key: "shipmentNo",
      header: t("shipments.no"),
      primary: true,
      render: (row) => row.shipmentNo,
    },
    {
      key: "status",
      header: t("case.status"),
      render: (row) => <ShipmentBadge status={row.status} />,
    },
    {
      key: "caseCount",
      header: t("shipments.caseCount"),
      align: "end",
      render: (row) => row.caseCount,
    },
    {
      key: "totalWeight",
      header: t("shipments.totalWeight"),
      align: "end",
      priority: "md",
      render: (row) => formatWeight(row.totalWeightG),
    },
    {
      key: "shippedOn",
      header: t("shipments.shippedOn"),
      priority: "md",
      render: (row) => nullable(row.shippedOn),
    },
    {
      key: "seinoNo",
      header: t("shipments.seinoNo"),
      priority: "lg",
      render: (row) => nullable(row.seinoNo),
    },
    {
      key: "invoiceNo",
      header: t("case.invoiceNo"),
      priority: "lg",
      render: (row) => nullable(row.invoiceNo),
    },
  ]

  const error = create.error ?? shipments.error ?? ready.error
  if (error && !shipments.data) return <ErrorNote error={error} />

  return (
    <>
      <PageHeader title={t("shipments.title")} />
      <ErrorNote error={error} />

      {!shipments.data ? (
        <Skeleton lines={5} />
      ) : (
        <>
          {canShip ? (
            <Card padded={false}>
              <CardHeader title={t("shipments.readyTitle")} icon="ship" />
              <DataTable
                columns={readyColumns}
                rows={ready.data?.items ?? []}
                rowKey={(row) => row.id}
                onRowClick={(row) => detail.open(row.id)}
                empty={<EmptyState icon="ship" title={t("shipments.readyEmpty")} />}
              />
            </Card>
          ) : null}

          <Card padded={false}>
            <CardHeader title={t("shipments.title")} icon="box" />
            <DataTable
              columns={shipmentColumns}
              rows={shipments.data.items}
              rowKey={(row) => row.id}
              onRowClick={(row) => void navigate(`/shipments/${row.id}`)}
              empty={<EmptyState icon="box" title={t("shipments.empty")} />}
            />
          </Card>
        </>
      )}

      {/* 고른 건이 있으면 화면 아래에 붙는다 — 긴 목록에서 버튼을 찾아 내려가지 않게. */}
      {picked.size > 0 ? (
        <div className="action-bar">
          <span className="action-bar__count">
            {t("shipments.selected", { count: picked.size })}
          </span>
          <Button variant="ghost" onClick={() => setPicked(new Set())}>
            {t("cases.clearFilter")}
          </Button>
          <Button variant="primary" loading={create.busy} onClick={() => void createShipment()}>
            {t("shipments.new")}
          </Button>
        </div>
      ) : null}

      {detail.panel}
    </>
  )
}
